import { Button } from '@genie-fintech/ui/components'
import { useCallback, useMemo, useState } from 'react'
import { Copy, CopyCheck, Download, KeyRound } from 'lucide-react'
import { useRedirectProxy, useRouteSummary } from '$contexts/RouteContext/hooks'
import { TOOLTIP_ID } from '$app/constants/common'
import { flexRow, flexColumn } from '$styles/common.css'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { ROUTE_NAMES } from '$router/config'
import { useBoolean, useToggle } from 'ahooks'
import { Icon } from '@genie-fintech/ui/icons'
import { TID } from '$services/api'
import LeavePageWarning from './LeavePageWarning'
import { modalContentBlock, modalContentCard } from '../styles.css'
import { title } from '@genie-fintech/ui/style/typography'
import { useTenantStore } from '$hooks/services/useTenants'
import { PASSWORD_LOGIN_URL_LABEL } from '$constants/themeTexts'

const { colors } = themeVars

interface IProps {
  name: string
  email: string
  password: string
  userId: TID
  userType: 'service' | 'dashboard'
}

const Label = ({ label }: { label: string }) => (
  <label className="text-xs font-medium" style={{ color: colors.neutral[60] }}>
    {label}
  </label>
)

const Value = ({ value }: { value: string }) => (
  <article
    className="text-sm font-semibold"
    style={{ color: colors.text.light }}
  >
    {value}
  </article>
)

type TCopyData = {
  name: string
  email: string
  url: string
  password: string
}

const formatCopyValue = (data: TCopyData) => {
  const lineBreak = '\r\n'

  const copyData = [
    { label: 'Name', value: data.name },
    { label: 'Email', value: data.email },
    { label: PASSWORD_LOGIN_URL_LABEL, value: data.url },
    { label: 'Password', value: data.password }
  ]

  return copyData
    .map(v => v.label + lineBreak + v.value + lineBreak)
    .join(lineBreak)
}

const PreviewScreen = ({ name, email, password, userId, userType }: IProps) => {
  const { route } = useRouteSummary()
  const { appId } = route.params

  const [isCopied, setIsCopied] = useState(false)

  const [hasViewPwd, { setTrue: setHasViewPwdTrue }] = useBoolean(false)

  const WEVIEW_URL = useTenantStore(
    state => state.currentTenant?.webview_url ?? ''
  )

  const [hasCopiedPassword, { setTrue: setHasCopiedPasswordTrue }] =
    useBoolean(false)

  const [doneDownload, setDoneDownload] = useState(false)

  const [showPassword, { toggle: toggleShowPassword }] = useToggle()

  const {
    routeData: {
      login: { pathname: loginPath }
    }
  } = useRouteSummary()

  const hasTakenAction =
    !password || isCopied || doneDownload || hasViewPwd || hasCopiedPassword

  const proxyRedirect = useRedirectProxy()

  const isDashboardUser = userType === 'dashboard'

  const LOGIN_URL = useMemo(() => {
    if (isDashboardUser) {
      return window.location.origin + loginPath
    }
    return WEVIEW_URL
  }, [isDashboardUser, loginPath, WEVIEW_URL])

  const onClickPassword = useCallback(() => {
    setHasViewPwdTrue()
    toggleShowPassword()
  }, [setHasViewPwdTrue, toggleShowPassword])

  const onDownloadCSV = useCallback(() => {
    const csvHeaders = ['Email', 'Password', PASSWORD_LOGIN_URL_LABEL].join(',')
    const csvRow = [email, `"${password}"`, LOGIN_URL].join(',')
    const csvContent = [csvHeaders, csvRow].join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    link.setAttribute('href', URL.createObjectURL(blob))
    link.setAttribute('download', `${name}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setDoneDownload(true)
  }, [name, email, password, LOGIN_URL])

  const onCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      const copiedValue = formatCopyValue({
        name,
        email,
        password,
        url: LOGIN_URL
      })

      // Create a Blob for the HTML content
      const htmlBlob = new Blob([copiedValue], { type: 'text/html' })
      // Create a Blob for the plain text content
      const textBlob = new Blob([copiedValue], { type: 'text/plain' })

      // Create a ClipboardItem with the appropriate MIME types
      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      })

      navigator.clipboard.writeText(copiedValue)

      navigator.clipboard.write([clipboardItem]).then(() => {
        setIsCopied(true)
      })
    },
    [name, email, password, LOGIN_URL]
  )

  const onGotoUserInfo = useCallback(() => {
    if (appId) {
      return proxyRedirect(
        ROUTE_NAMES.APP_USER_DETAIL,
        ({ params, ...options }) => ({
          ...options,
          params: { ...params, userId }
        })
      )
    }

    if (isDashboardUser) {
      return proxyRedirect(
        ROUTE_NAMES.GLOBAL_MEMBER_DETAIL,
        ({ params, ...options }) => ({
          ...options,
          params: { ...params, memberId: userId }
        })
      )
    }

    return proxyRedirect(
      ROUTE_NAMES.GLOBAL_SERVICE_USER_DETAIL,
      ({ params, ...options }) => ({
        ...options,
        params: { ...params, serviceUserId: userId }
      })
    )
  }, [appId, isDashboardUser, userId, proxyRedirect])

  const onCopyPassword = useCallback(() => {
    navigator.clipboard.writeText(password).then(setHasCopiedPasswordTrue)
  }, [setHasCopiedPasswordTrue, password])

  return (
    <>
      <article className={modalContentBlock} style={{ gap: 0 }}>
        <article className={modalContentCard}>
          <h3
            className={title.two}
            style={{ color: colors.text.light, textTransform: 'uppercase' }}
          >
            Retrieve Password and Information
          </h3>

          <main
            className={flexRow}
            style={{
              flexDirection: 'row-reverse',
              gap: 16,
              alignItems: 'start'
            }}
          >
            <article>
              <Button
                styleVariants={{ type: 'text', size: 'small' }}
                data-tooltip-id={TOOLTIP_ID}
                data-tooltip-content={isCopied ? 'Copied' : 'Copy'}
                onClick={onCopy}
              >
                Copy to Clipboard
                {isCopied ? <CopyCheck size={14} /> : <Copy size={14} />}
              </Button>
            </article>

            <article className={flexColumn} style={{ gap: 16, flex: 1 }}>
              <article className={flexColumn} style={{ gap: 2 }}>
                <Label label="Name" />
                <Value value={name} />
              </article>

              <article className={flexColumn} style={{ gap: 2 }}>
                <Label label="Email Address" />
                <Value value={email} />
              </article>

              <article className={flexColumn} style={{ gap: 2 }}>
                <Label label={PASSWORD_LOGIN_URL_LABEL} />
                <Value value={LOGIN_URL} />
              </article>

              <article className={flexColumn} style={{ gap: 2 }}>
                <Label label="Password" />
                {password ? (
                  <article className={flexRow} style={{ gap: 10 }}>
                    {showPassword && <Value value={password} />}

                    {!showPassword && (
                      <article className={flexRow} style={{ gap: 2 }}>
                        {new Array(password.length).fill(null).map((_, k) => (
                          <article
                            key={k}
                            style={{
                              display: 'inline-grid',
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              background: colors.text.light
                            }}
                          />
                        ))}
                      </article>
                    )}

                    <button onClick={onClickPassword}>
                      {
                        <Icon
                          namespace={showPassword ? 'EyeOn' : 'EyeOff'}
                          color="text.light"
                          width={20}
                        />
                      }
                    </button>

                    <button
                      data-tooltip-id={TOOLTIP_ID}
                      data-tooltip-content={
                        hasCopiedPassword ? 'Copied' : 'Copy'
                      }
                      onClick={onCopyPassword}
                    >
                      {hasCopiedPassword ? (
                        <CopyCheck size={20} />
                      ) : (
                        <Copy size={20} />
                      )}
                    </button>
                  </article>
                ) : (
                  <article
                    className={flexRow}
                    style={{ color: colors.text.light, gap: 6 }}
                  >
                    <KeyRound size={16} />
                    <Value
                      value={'The password is encrypted and can not be seen.'}
                    />
                  </article>
                )}
              </article>
            </article>
          </main>
        </article>

        <article
          className={flexRow}
          style={{ gap: 8, padding: '12px 16px', justifyContent: 'flex-end' }}
        >
          {!doneDownload && password && (
            <Button
              styleVariants={{ kind: 'neutral', type: 'outlined' }}
              onClick={onDownloadCSV}
            >
              Download .csv File
              <Download />
            </Button>
          )}

          <Button onClick={onGotoUserInfo}>Go to User Profile</Button>
        </article>
      </article>

      <LeavePageWarning showWhen={!hasTakenAction} />
    </>
  )
}

export default PreviewScreen
